import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Button, Box, Paper, CircularProgress, Grid
} from '@mui/material';
import { aiEditParagraph, aiWriteNextParagraph } from '../utils/api';
import { t } from '@lingui/macro';
import { TextSelection } from 'prosemirror-state';
import { DOMParser as ProseMirrorDOMParser } from 'prosemirror-model';
import { logEvent } from "firebase/analytics";
import { getAnalyticsInstance } from "../services/firebase-config";

const AIParagraphDialog = ({
  open,
  onClose,
  storyID,
  chapterIndex,
  paragraphText,
  paragraphPos,
  chapterText,
  editor,
  actionType
}) => {
  const [mode, setMode] = useState(null); // 'edit' | 'continue'
  const [loading, setLoading] = useState(false);
  const [generatedText, setGeneratedText] = useState('');

  const handleAction = async (type) => {
    setMode(type);
    setLoading(true);
    try {
      const params = { storyID, chapterIndex, paragraphText, chapterText };
      const result =
        type === 'edit'
          ? await aiEditParagraph(params)
          : await aiWriteNextParagraph(params);
      setGeneratedText(result);
    } catch (err) {
      console.error('AI generation error:', err);
      setGeneratedText(t`Failed to generate content.`);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    const { state, view, schema } = editor;
    const tr = state.tr;
    let pos = paragraphPos;

    const analytics = getAnalyticsInstance();
    if (analytics?.app) {
      logEvent(analytics, `ai_paragraph_apply_${mode}`, { storyID, chapterIndex });
    }

    console.log('[Mobile Debug] Apply clicked');

    // Step 1: Revalidate paragraph position
    const isValidPos = () => {
      if (pos == null || pos >= state.doc.content.size) return false;
      const node = state.doc.nodeAt(pos);
      return node?.type.name === 'paragraph' && node.textContent === paragraphText;
    };

    if (!isValidPos()) {
      pos = null;
      state.doc.descendants((node, currentPos) => {
        if (pos != null) return false;
        if (node.type.name === 'paragraph' && node.textContent === paragraphText) {
          pos = currentPos;
          return false;
        }
        return true;
      });

      if (pos == null) {
        alert(t`This paragraph was edited or removed. Please try again.`);
        return;
      }
    }

    // Step 2: Convert AI text into paragraphs
    const convertPlainTextToHTML = (text) => {
      return text
        .trim()
        .split(/\n{2,}/) // split by double newline for paragraphs
        .map(p => `<p>${p.trim().replace(/\n/g, ' ')}</p>`)
        .join('');
    };

    const html = convertPlainTextToHTML(generatedText);
    const parser = ProseMirrorDOMParser.fromSchema(schema);
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    const slice = parser.parseSlice(tempDiv, { preserveWhitespace: true });

    // Step 3: Insert
    let targetPos;
    if (mode === 'edit') {
      tr.replaceWith(pos, pos + state.doc.nodeAt(pos).nodeSize, slice.content);
      targetPos = pos;
    } else if (mode === 'continue') {
      targetPos = pos + state.doc.nodeAt(pos).nodeSize;
      tr.insert(targetPos, slice.content);
    }

    // Step 4: Track inserted nodes and build highlight ranges
    let highlightDecos = [];
    let lastNodeEnd = targetPos;
    slice.content.forEach((node) => {
      const nodeSize = node.nodeSize;
      highlightDecos.push({ from: lastNodeEnd, to: lastNodeEnd + nodeSize });
      lastNodeEnd += nodeSize;
    });
    tr.setMeta('highlight-new-multi', highlightDecos);
    view.dispatch(tr);


    // Step 5: scroll & selection inside last inserted paragraph
    const endOfInsert = targetPos + slice.content.size;
    const resolvedPos = view.state.doc.resolve(Math.max(0, endOfInsert - 1));
    const selection = TextSelection.near(resolvedPos, -1);
    view.dispatch(view.state.tr.setSelection(selection));

    setTimeout(() => {
      const dom = view.domAtPos(selection.from);
      const el = dom?.node?.nodeType === 3 ? dom.node.parentElement : dom.node;
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 250);


    // Step 6: restore focus
    setTimeout(() => {
      editor.commands.focus();
    }, 50);

    // Step 7: remove highlight later
    setTimeout(() => {
      editor.view.dispatch(editor.state.tr.setMeta('remove-highlight', true));
    }, 1800);

    // Step 8: close dialog
    onClose();
  };


  const handleCancel = () => {
    const analytics = getAnalyticsInstance();
    if (analytics?.app) {
      logEvent(analytics, `ai_paragraph_cancel_${mode}`, { storyID, chapterIndex });
    }
    console.log('[Mobile Debug] Cancel clicked');

    setMode(null);
    setGeneratedText('');
    setLoading(false);
    onClose();
  };

  useEffect(() => {
    if (open && actionType && !mode) {
      setGeneratedText('');
      handleAction(actionType);
    }
  }, [open, actionType, mode]);

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      {!mode && (
        <>
          <DialogTitle>{t`AI Assistant`}</DialogTitle>
          <DialogContent>
            <Typography gutterBottom>{t`What would you like to do?`}</Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <Button variant="contained" onClick={() => handleAction('edit')}>{t`Edit This Paragraph`}</Button>
              <Button variant="outlined" onClick={() => handleAction('continue')}>{t`Write The Next Paragraph`}</Button>
            </Box>
          </DialogContent>
        </>
      )}

      {loading && (
        <DialogContent sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress color="primary" />
          <Typography sx={{ mt: 2 }}>{t`AI is generating content…`}</Typography>
        </DialogContent>
      )}

      {!loading && mode && generatedText && (
        <>
          <DialogTitle>{t`Preview`}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              {mode === 'edit' && (
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">{t`Original`}</Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 0.5, height: '100%' }}>{paragraphText}</Paper>
                </Grid>
              )}
              <Grid item xs={12} md={mode === 'edit' ? 6 : 12}>
                <Typography variant="caption" color="text.secondary">{t`AI Suggestion`}</Typography>
                <Paper variant="outlined" sx={{ p: 2, mt: 0.5, height: '100%' }}>
                  {generatedText.trim().split(/\n{2,}/).map((para, idx) => (
                    <Typography key={idx} paragraph sx={{ mb: 1 }}>
                      {para.trim()}
                    </Typography>
                  ))}
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button
              variant="contained"
              onClick={handleCancel}
              sx={{
                backgroundColor: '#FF0004',
                color: 'error.contrastText',
                '&:hover': {
                  backgroundColor: '#FF0004',
                },
              }}
            >
              {t`Cancel`}
            </Button>

            <Button
              variant="contained"
              onClick={handleApply}
              sx={{
                backgroundColor: 'success.main',
                color: 'contrastText',
                '&:hover': {
                  backgroundColor: 'success.dark',
                },
              }}
            >
              {t`Apply`}
            </Button>


          </DialogActions>
        </>
      )}
      <Box
        sx={{
          position: 'absolute',
          bottom: 8,
          left: 16,
          pointerEvents: 'none',
        }}
      >
        <Typography variant="caption" color="text.secondary" size="small" >
          AI-Assistant-v0.1.0-beta
        </Typography>
      </Box>

    </Dialog>
  );
};

export default AIParagraphDialog;
